import React, { Component } from 'react';
import classNames from 'classnames';
import * as R from 'ramda';
import PropTypes from 'prop-types';
import Button from 'reactstrap/lib/Button';

import { USER_FIELDS } from 'core/constants';
import { getIntlKeyByEnv } from 'core/helpers';
import { FormattedHTMLMessageSecure, FormattedTag, RouteLink } from 'core/components';
import { withModalActions, withUser, withBonusActions } from 'core/hocs';

import { BONUSES_ACTION_TYPES, MODAL_IDS } from '../../../constants';
import { getBackgroundStyles } from '../../../helpers/images';
import { withPixelRatio } from '../../../hocs/with-pixel-ratio';

import './promo-bonus-card.scss';

export class PromoBonusCardUI extends Component {
  static propTypes = {
    item: PropTypes.shape().isRequired,
    className: PropTypes.string,
    userCurrency: PropTypes.string.isRequired,
    currencyCode: PropTypes.string.isRequired,
    openModal: PropTypes.func.isRequired,
    openModalAndCloseOthers: PropTypes.func.isRequired,
    isUserRegistered: PropTypes.bool.isRequired,
    isFirstItem: PropTypes.bool,
    isUserLoggedIn: PropTypes.bool.isRequired,
    isSignUpCompleted: PropTypes.bool.isRequired,
    setPendingBonus: PropTypes.func.isRequired,
    pixelRatio: PropTypes.string.isRequired,
  };

  static defaultProps = {
    className: '',
    isFirstItem: false,
  };

  showInfo = () => {
    const { item: { bonusId, withoutAction }, openModal } = this.props;

    if (!withoutAction && this.getValueFromCurrencies(bonusId)) {
      openModal(this.getValueFromCurrencies(bonusId));
    }
  };

  openModal = () => {
    const {
      isUserRegistered,
      isUserLoggedIn,
      isSignUpCompleted,
      openModalAndCloseOthers,
    } = this.props;

    let modalId;

    if (!isUserRegistered && !isUserLoggedIn) {
      modalId = MODAL_IDS.SIGN_UP;
    } else if (isUserRegistered && !isUserLoggedIn) {
      modalId = MODAL_IDS.SIGN_IN;
    } else if (isUserLoggedIn && !isSignUpCompleted) {
      modalId = MODAL_IDS.COMPLETE_SIGN_UP;
    } else if (isUserLoggedIn && isSignUpCompleted) {
      modalId = MODAL_IDS.DEPOSIT;
    }

    openModalAndCloseOthers(modalId);
  };

  activateBonus = (e) => {
    const {
      isUserLoggedIn,
      isSignUpCompleted,
      setPendingBonus,
      item: { bonusId, minDeposit, withoutAction },
    } = this.props;

    e.stopPropagation();

    if (!withoutAction && this.getValueFromCurrencies(bonusId) && this.getValueFromCurrencies(minDeposit)) {
      if (isUserLoggedIn && isSignUpCompleted) {
        setPendingBonus({
          bonusPlanId: this.getValueFromCurrencies(bonusId),
          minAmount: this.getValueFromCurrencies(minDeposit),
        });
      }

      this.openModal();
    }
  };

  getValueFromCurrencies = (obj) => {
    const {
      userCurrency,
    } = this.props;

    return R.path(['currencies', userCurrency])(obj);
  };

  render() {
    const {
      item,
      isFirstItem,
      className,
      pixelRatio,
      currencyCode,
    } = this.props;

    const {
      commonId,
      title,
      description,
      withoutAction,
      buttonTitle,
      descriptionColor,
      mobileDescriptionColor,
      titleColor,
      mobileTitleColor,
      buttonTitleColor,
      buttonColor,
      actionType,
      buttonUrl,
      isNewTabLink,
      backgroundImageUrl,
      backgroundImageUrlMobile,
      backgroundImageUrlTablet,
      imageUrl,
      imageUrlMobile,
      imageUrlTablet,
    } = item;

    const backgroundImage = getBackgroundStyles(backgroundImageUrl, pixelRatio, backgroundImageUrl);
    const backgroundImageMobile = getBackgroundStyles(backgroundImageUrlMobile, pixelRatio, backgroundImageUrl);
    const backgroundImageTablet = getBackgroundStyles(backgroundImageUrlTablet, pixelRatio, backgroundImageUrl);

    const characterStyles = getBackgroundStyles(imageUrl, pixelRatio, imageUrl);
    const characterTabletStyles = getBackgroundStyles(imageUrlTablet, pixelRatio, imageUrl);
    const characterMobileStyles = getBackgroundStyles(imageUrlMobile, pixelRatio, imageUrl);
    const isBtnWithLink = buttonUrl && actionType === BONUSES_ACTION_TYPES.LINK;
    const isRelativeLink = (buttonUrl || '').startsWith('/');
    const getLabelColor = (isMobile, regularColor, mobileColor) => (isMobile ? mobileColor : regularColor) || '#000';

    const titleElement = isMobile => (
      <div
        style={{ color: getLabelColor(isMobile, titleColor, mobileTitleColor) }}
        className={classNames('bonus-card-title h2 mb-1 font-family-oswald', {
          first: isFirstItem,
          'font-family-oswald text-uppercase': !isFirstItem,
          'd-block d-sm-none': isMobile,
          'd-none d-sm-block': !isMobile,
        })}
      >
        <FormattedTag
          tag="span"
          id={title.intlKey ? getIntlKeyByEnv(title.intlKey) : 'no-text'}
          values={{ amount: this.getValueFromCurrencies(title), currencyCode }}
          isHtml
        />
      </div>
    );

    const descriptionElement = isMobile => (
      <div
        className={classNames('bonus-card-description text-base', {
          first: isFirstItem,
          'd-block d-sm-none': isMobile,
          'd-none d-sm-block': !isMobile,
        })}
        style={{ color: getLabelColor(isMobile, descriptionColor, mobileDescriptionColor) }}
      >
        <FormattedHTMLMessageSecure
          id={description.intlKey ? getIntlKeyByEnv(description.intlKey) : 'no-text'}
          values={{ amount: this.getValueFromCurrencies(description), currencyCode }}
        />
      </div>
    );

    return (
      <div
        className={classNames('bonus-card d-flex flex-column position-relative rounded overflow-hidden', className, {
          'without-action': withoutAction,
          'bonus-card-sm pb-8_5 pb-sm-3': !isFirstItem,
          'pb-8_5 pb-sm-0': isFirstItem,
        })}
      >
        {/* DESKTOP */}
        <div
          className={classNames('bonus-card-bg position-relative d-none d-md-block w-100', {
            'mb-2 mb-sm-4': !isFirstItem,
            'first mb-0': isFirstItem,
          })}
          style={backgroundImage}
        >
          <div
            style={characterStyles}
            className={classNames('character-image position-absolute d-none d-md-block', {
              first: isFirstItem,
            })}
          />
        </div>

        {/* TABLET */}
        <div
          className={classNames('bonus-card-bg position-relative d-none d-sm-block d-md-none overflow-hidden w-100 mb-2', {
            first: isFirstItem,
          })}
          style={backgroundImageTablet}
        >
          <div
            style={characterTabletStyles}
            className={classNames('character-image position-absolute d-none d-sm-block d-md-none', {
              first: isFirstItem,
            })}
          />
        </div>

        {/* MOBILE */}
        <div
          className="bonus-card-bg position-relative d-block d-sm-none overflow-hidden w-100 mb-2"
          style={backgroundImageMobile}
        >
          <div
            style={characterMobileStyles}
            className="character-image position-absolute d-sm-none"
          />
        </div>

        <div
          className={classNames('bonus-card-content px-2 px-sm-4', {
            'first d-sm-flex flex-column align-items-start justify-content-center pl-sm-11': isFirstItem,
          })}
        >
          {titleElement(true)}
          {titleElement(false)}

          {descriptionElement(true)}
          {descriptionElement(false)}

          <div
            className={classNames('bonus-card-button-panel d-flex mt-2_5 mt-md-4_25', {
              first: isFirstItem,
            })}
          >
            {!withoutAction ? (
              <>
                <FormattedTag
                  id={getIntlKeyByEnv(buttonTitle?.intlKey || '')}
                  {...(isBtnWithLink
                    ? {
                      tag: isRelativeLink ? RouteLink : 'a',
                      ...{ [isRelativeLink ? 'to' : 'href']: buttonUrl },
                      ...(isNewTabLink ? {
                        target: '_blank',
                        rel: 'noopener noreferrer',
                      } : {}),
                    } : {
                      tag: Button,
                      onClick: this.activateBonus,
                      color: 'light',
                    }
                  )}
                  style={{ color: buttonTitleColor, background: buttonColor }}
                  className={classNames('bonus-card-button-panel-item mr-0_75 text-truncate', {
                    'btn btn-light': isBtnWithLink,
                    first: isFirstItem,
                  })}
                />

                <FormattedTag
                  id="read-more"
                  tag={RouteLink}
                  to={`/bonus/${commonId}`}
                  className={classNames('bonus-card-button-panel-item d-flex align-items-center justify-content-center text-center d-block btn promo-outline-button text-truncate ml-0_75', {
                    first: isFirstItem,
                  })}
                />
              </>
            ) : (
              <FormattedTag
                id="promo-bonuses.coming-soon"
                className="without-action-text text-uppercase font-weight-bold text-small position-relative"
              />
            )}
          </div>
        </div>
      </div>
    );
  }
}

export const PromoBonusCard = withModalActions(
  withBonusActions(
    withUser(withPixelRatio(PromoBonusCardUI), [
      USER_FIELDS.IS_USER_LOGGED_IN,
      USER_FIELDS.IS_SIGN_UP_COMPLETED,
      USER_FIELDS.IS_USER_REGISTERED,
      USER_FIELDS.USER_TRACKING_CODES,
      USER_FIELDS.USER_BALANCE,
    ]),
  ),
);
